/**
 * 六边拼图 - 主界面交互逻辑
 */

import { i18n } from './core/I18nManager';
import { AuthModal } from './ui/AuthModal';
import { Navbar } from './ui/Navbar';
import { PuzzleCard } from './ui/PuzzleCard';
import { ApiClient } from './core/ApiClient';
import { SaveManager } from './storage/SaveManager';
import './styles/main.css';

// ============================================
// 状态
// ============================================
let navbar: Navbar;
let currentCategory = 'all';
let currentSearch = '';
let selectedPictureId: string | null = null;
let latestSaveId: string | null = null;
let currentPage = 1;
const itemsPerPage = 12;

// ============================================
// 动态布局调整
// ============================================
function switchCategory(category: string): void {
    currentCategory = category;
    currentPage = 1; // 重置为第一页
    
    // Update UI
    $$('.filter-btn').forEach(btn => {
        const btnCat = (btn as HTMLElement).dataset.category;
        if (btnCat === category) {
            btn.classList.add('active', 'text-stone-900', 'border-b', 'border-stone-900');
            btn.classList.remove('text-stone-400');
        } else {
            btn.classList.remove('active', 'text-stone-900', 'border-b', 'border-stone-900');
            btn.classList.add('text-stone-400');
        }
    });

    renderPictureGrid();
}

function updateLayoutMode(): void {
    const heroRow = $('.hero-row');
    const resumeCard = $('#resumeCard');
    const saveManager = SaveManager.getInstance();
    const latestSave = saveManager.getLatestSave();
    
    if (heroRow && resumeCard) {
        if (!latestSave) {
            heroRow.classList.add('no-resume');
            (resumeCard as HTMLElement).style.display = 'none';
            latestSaveId = null;
        } else {
            heroRow.classList.remove('no-resume');
            (resumeCard as HTMLElement).style.display = 'flex';
            latestSaveId = latestSave.puzzleId || null;
            
            // 更新继续游戏的 UI
            const img = resumeCard.querySelector('img');
            const title = resumeCard.querySelector('h4');
            const desc = resumeCard.querySelector('p');
            
            if (img && latestSave.imageData) {
                img.src = latestSave.imageData;
            } else if (img && latestSave.puzzleId) {
                // 如果没有快照，尝试获取图片 URL (实际应用中存档应包含预览图)
                // 这里我们暂且显示默认
            }
            
            const placedCount = latestSave.pieces.filter(p => p.isPlaced).length;
            const totalCount = latestSave.pieces.length;
            const progress = Math.round((placedCount / totalCount) * 100);
            
            if (title) title.textContent = i18n.t('resume_title');
            if (desc) desc.textContent = i18n.t('resume_desc', { 
                progress: progress, 
                name: latestSave.puzzleName || i18n.t('pic_generic') 
            });
        }
    }
}

// ============================================
// DOM 缓存
// ============================================
const $ = (selector: string) => document.querySelector(selector);
const $$ = (selector: string) => document.querySelectorAll(selector);

// ============================================
// 图库渲染 (API 驱动版本)
// ============================================
async function renderPictureGrid(): Promise<void> {
    const grid = $('#pictureGrid') as HTMLElement;
    const paginationContainer = $('#paginationControls') as HTMLElement;
    if (!grid) return;

    // 加载动画
    grid.style.opacity = '0.5';

    try {
        const data = await ApiClient.get<any>('/api/puzzle/list', {
            category: currentCategory,
            search: currentSearch,
            page: currentPage.toString(),
            limit: itemsPerPage.toString()
        });

        const puzzlesList: any[] = data.puzzles;
        
        if (puzzlesList.length === 0) {
            grid.innerHTML = `<div class="col-span-full py-20 text-center text-stone-400">
                <iconify-icon icon="solar:camera-minimalistic-linear" width="48" class="mb-2 opacity-20"></iconify-icon>
                <p>未找到相关拼图</p>
            </div>`;
            if (paginationContainer) (paginationContainer as HTMLElement).style.display = 'none';
            grid.style.opacity = '1';
            return;
        }

        grid.innerHTML = puzzlesList.map((pic) => PuzzleCard.render(pic)).join('');

        // 没有 slug 的旧数据卡片：点击弹出难度选择框
        grid.querySelectorAll<HTMLAnchorElement>('.puzzle-card').forEach(card => {
            if (card.getAttribute('href') === 'javascript:void(0)') {
                card.addEventListener('click', (e) => {
                    e.preventDefault();
                    const id = card.dataset.id;
                    if (id) openDifficultyModal(id);
                });
            }
        });

        updatePaginationUI(data.has_more, currentPage, Math.ceil(data.total_count / itemsPerPage));
        grid.style.opacity = '1';

    } catch (error) {
        console.error('Failed to load puzzles:', error);
        grid.innerHTML = `<p class="col-span-full py-10 text-center text-red-400">加载失败</p>`;
    }
}

function updatePaginationUI(hasMore: boolean, page: number, totalPages: number): void {
    const container = $('#paginationControls');
    if (!container) return;

    // 只有一页且没有更多数据时隐藏分页
    if (!hasMore && page === 1) {
        (container as HTMLElement).style.display = 'none';
        return;
    }

    (container as HTMLElement).style.display = 'block';
    container.innerHTML = `
        <div class="flex items-center justify-center gap-6 mt-10">
            <button id="prevPage" class="w-12 h-12 flex items-center justify-center rounded-full border border-stone-200 bg-white text-stone-600 hover:bg-stone-100 transition-all shadow-sm disabled:opacity-30 disabled:cursor-not-allowed" ${page === 1 ? 'disabled' : ''}>
                <iconify-icon icon="solar:arrow-left-linear" width="24"></iconify-icon>
            </button>
            
            <div class="flex items-center gap-2">
                <span class="text-sm font-bold text-stone-900">${page}</span>
                <span class="text-xs text-stone-400">/</span>
                <span class="text-xs text-stone-400">${totalPages}</span>
            </div>

            <button id="nextPage" class="w-12 h-12 flex items-center justify-center rounded-full border border-stone-200 bg-white text-stone-600 hover:bg-stone-100 transition-all shadow-sm disabled:opacity-30 disabled:cursor-not-allowed" ${!hasMore ? 'disabled' : ''}>
                <iconify-icon icon="solar:arrow-right-linear" width="24"></iconify-icon>
            </button>
        </div>
    `;

    // 绑定翻页事件
    container.querySelector('#prevPage')?.addEventListener('click', () => {
        if (currentPage > 1) {
            currentPage--;
            renderPictureGrid();
            // 滚动回图库顶部，提升交互体验
            $('#pictureGrid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    });

    container.querySelector('#nextPage')?.addEventListener('click', () => {
        if (hasMore) {
            currentPage++;
            renderPictureGrid();
            $('#pictureGrid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    });
}

// ============================================
// 难度弹窗
// ============================================
function openDifficultyModal(pictureId: string): void {
    selectedPictureId = pictureId;
    const modal = $('#difficultyModal');
    
    // Reset selection
    $$('.diff-option').forEach(btn => btn.classList.remove('selected'));
    const defaultDiff = $('.diff-option[data-diff="easy"]');
    if (defaultDiff) defaultDiff.classList.add('selected');

    modal?.classList.add('active');
    document.body.style.overflow = 'hidden';
}

function closeDifficultyModal(): void {
    $('#difficultyModal')?.classList.remove('active');
    document.body.style.overflow = '';
    selectedPictureId = null;
}

function setupDifficultySelect() {
    $$('.diff-option').forEach(btn => {
        btn.addEventListener('click', () => {
            $$('.diff-option').forEach(b => b.classList.remove('selected'));
            btn.classList.add('selected');
        });
    });
}

function startGame(): void {
    if (!selectedPictureId) return;

    const diffBtn = document.querySelector<HTMLElement>('.diff-option.selected');
    const difficulty = diffBtn?.dataset.diff || 'easy';
    const rotation = ($('#rotationToggle') as HTMLInputElement)?.checked ? '1' : '0';

    window.location.href = `game.html?id=${selectedPictureId}&difficulty=${difficulty}&rotation=${rotation}`;
}

// ============================================
// 主题切换
// ============================================
function toggleTheme(): void {
    const current = document.documentElement.dataset.theme;
    const next = current === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = next;
    localStorage.setItem('theme', next);
}

// ============ 每日推荐逻辑 (Daily Zen) ============
async function initHeroCard(): Promise<void> {
    const heroCard = $('#heroCard') as HTMLElement;
    const heroImg = heroCard?.querySelector('img');
    const dailyZenLabel = $('[data-i18n="daily_zen"]');
    
    if (!heroCard) return;

    // 1. 设置日期文案
    const now = new Date();
    const dateStr = `${now.getMonth() + 1}月${now.getDate()}日`;
    if (dailyZenLabel) {
        dailyZenLabel.textContent = `Zen · ${dateStr}`;
    }

    // 2. 检查缓存
    const CACHE_KEY = 'daily_puzzle_cache';
    const today = now.toDateString(); // 例如 "Fri Feb 06 2026"
    
    let dailyPuzzle: any = null;
    const cached = localStorage.getItem(CACHE_KEY);
    
    if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.date === today) {
            dailyPuzzle = parsed.data;
        }
    }

    // 3. 如果无缓存或已过期，请求后端
    if (!dailyPuzzle) {
        try {
            dailyPuzzle = await ApiClient.get('/api/puzzle/random');
            localStorage.setItem(CACHE_KEY, JSON.stringify({
                date: today,
                data: dailyPuzzle
            }));
        } catch (e) {
            console.error('Failed to fetch daily puzzle', e);
            // 失败时使用兜底默认 ID
            dailyPuzzle = { puzzle_id: 's_10001', image_url: 'assets/picture/10001.jpg' };
        }
    }

    // 4. 渲染 Hero UI
    heroCard.dataset.id = dailyPuzzle.puzzle_id;
    if (heroImg) {
        heroImg.src = dailyPuzzle.image_url;
        heroImg.alt = dailyPuzzle.puzzle_name || 'Daily Zen';
    }
}

// ============================================
// 初始化
// ============================================
async function init(): Promise<void> {
    // 初始化多语言
    await i18n.init();
    
    // 初始化导航栏
    const navbarRoot = document.getElementById('navbar-root');
    if (navbarRoot) {
        navbar = new Navbar(navbarRoot);
    }

    // 初始化每日推荐
    await initHeroCard();

    // 加载保存的主题
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) document.documentElement.dataset.theme = savedTheme;

    updateLayoutMode();

    // 渲染图库
    renderPictureGrid();
    setupDifficultySelect();

    // 搜索监听 (来自 Navbar 的自定义事件)
    window.addEventListener('nav-search', (e: any) => {
        currentSearch = e.detail;
        currentPage = 1;
        renderPictureGrid();
    });

    // 分类标签
    $$('.filter-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const cat = (btn as HTMLElement).dataset.category;
            if (cat) switchCategory(cat);
        });
    });

    // Hero 按钮
    $('#heroPlayBtn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = ($('#heroCard') as HTMLElement)?.dataset.id;
        if (id) openDifficultyModal(id);
    });
    
    $('#heroCard')?.addEventListener('click', () => {
        const id = ($('#heroCard') as HTMLElement)?.dataset.id;
        if (id) openDifficultyModal(id);
    });

    // 继续游戏
    $('#resumeCard')?.addEventListener('click', () => {
        if (latestSaveId) {
            window.location.href = `game.html?id=${latestSaveId}&resume=1`;
        }
    });
    $('#resumeBtn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        if (latestSaveId) {
            window.location.href = `game.html?id=${latestSaveId}&resume=1`;
        }
    });

    // 上传按钮 - 修改为跳转到 upload.html
    $('#uploadBtn')?.addEventListener('click', (e) => {
        e.preventDefault();
        window.location.href = 'upload.html';
    });

    // 难度弹窗关闭 (点击遮罩)
    $('.zen-modal-overlay')?.addEventListener('click', (e) => {
        if (e.target === $('.zen-modal-overlay')) {
            closeDifficultyModal();
        }
    });

    $('#startGameBtn')?.addEventListener('click', startGame);

    // ESC 关闭
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeDifficultyModal();
    });
}

// 启动
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
